import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

// Date formatters
export const formatDate = (date: string | Date, format = 'MMM D, YYYY'): string => {
  return dayjs(date).format(format);
};

export const formatDateTime = (date: string | Date, format = 'MMM D, YYYY h:mm A'): string => {
  return dayjs(date).format(format);
};

export const formatRelativeTime = (date: string | Date): string => {
  return dayjs(date).fromNow();
};

export const formatTime = (time: string, format = 'h:mm A'): string => {
  if (!time) return '';
  // If it's already a formatted time string, return as-is
  if (time.includes(':') && !time.includes('T')) {
    return time;
  }
  return dayjs(time).format(format);
};

export const formatShortDate = (date: string | Date): string => {
  if (!date) return '';
  return dayjs(date).format('MMM D, YYYY');
};

// Number formatters
export const formatNumber = (num: number): string => {
  return new Intl.NumberFormat('en-IN').format(num);
};

export const formatCurrency = (amount: number, currency = 'INR'): string => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(amount);
};

export const formatPercentage = (value: number, decimals = 1): string => {
  return `${value.toFixed(decimals)}%`;
};

export const formatCompactNumber = (num: number): string => {
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`;
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}K`;
  }
  return num.toString();
};

// String formatters
export const capitalizeFirst = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const capitalizeWords = (str: string): string => {
  return str.split(' ').map(capitalizeFirst).join(' ');
};

export const truncate = (str: string, maxLength: number): string => {
  if (str.length <= maxLength) return str;
  return `${str.slice(0, maxLength)}...`;
};

export const getInitials = (name: string): string => {
  const parts = name.trim().split(' ').filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
};

// Phone formatter
export const formatPhone = (phone: string): string => {
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `${cleaned.slice(0, 5)} ${cleaned.slice(5)}`;
  }
  return phone;
};

// File size formatter
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

// Check if date is valid (not default "0001-01-01T00:00:00")
export const isValidDate = (dateString?: string): boolean => {
  if (!dateString) return false;
  return !dateString.startsWith('0001-01-01');
};

// Format date for Indian locale (e.g., "12 Jan, 2024")
export const formatDateIndian = (dateString?: string): string => {
  if (!dateString) return '-';
  try {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return dateString;
  }
};

// Format date for Indian locale with time (e.g., "12 Jan, 2024, 02:30 pm")
export const formatDateIndianDetailed = (dateString?: string): string => {
  if (!dateString) return '-';
  try {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateString;
  }
};

// Format date for US locale (e.g., "Jan 12, 2024")
export const formatDateUS = (dateString?: string): string => {
  if (!dateString) return 'N/A';
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return dateString;
  }
};

// Format time string (returns as-is or empty string)
export const formatTimeString = (timeString?: string): string => {
  if (!timeString) return '';
  return timeString;
};

// Helper to get filename from URL
export const getFilenameFromUrl = (url: string): string => {
  try {
    const urlObj = new URL(url);
    const pathname = decodeURIComponent(urlObj.pathname);
    return pathname.split('/').pop() || 'document';
  } catch {
    return url.split('/').pop() || 'document';
  }
};

// Helper to get readable document type title
export const getReadableDocumentTitle = (type?: string): string => {
  if (!type) return 'Document';
  switch (type.toLowerCase()) {
    case 'adharcard':
    case 'aadharcard':
    case 'aadhaar':
    case 'aadhar':
      return 'Aadhar Card';
    case 'collegeid':
    case 'college_id':
      return 'College ID';
    case 'passport':
      return 'Passport';
    case 'nursingdocument':
      return 'Nursing Document';
    case 'pancard':
    case 'pan':
      return 'PAN Card';
    case 'drivinglicense':
    case 'driving_license':
      return 'Driving License';
    case 'voterid':
    case 'voter_id':
      return 'Voter ID';
    default:
      // Capitalize first letter and add spaces before capital letters
      return type
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (str) => str.toUpperCase())
        .trim();
  }
};

// Format profile percentage - show up to 2 decimal places, remove trailing zeros
export const formatProfilePercentage = (value?: number): string => {
  if (value === undefined) return '0';
  const fixed = Number(value).toFixed(2);
  // Remove trailing zeros after decimal point
  return fixed.replace(/\.?0+$/, '');
};

// Format relative time ago (e.g., "Just now", "5m ago", "2d ago")
export const formatTimeAgo = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) return 'Just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};
